import React, { useState } from "react";

import {
  ArrowButton,
  Typography,
  TextField,
  Button,
  Modal,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as DeleteRequest } from "@king-ict/eupisi-ui/components/Icon/DeleteRequest.svg";
import { ReactComponent as FaceMan } from "@king-ict/eupisi-ui/components/Icon/FaceMan.svg";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { deleteRequest as deleteRequestApi } from "../../api";
import {
  editRequestUrl,
  requestsRoute,
  subsequentRequestsRoute,
  preschoolRequestsRoute,
  editPreschoolRequestUrl,
} from "../../app/routes";
import { useBusinessRule } from "../../hooks";
import {
  EnrollmentTypeEnum,
  RequestPersonInfo,
  RequestTypeEnum,
} from "../../models";
import styles from "./RequestLayout.module.scss";

export interface RequestLayoutProps {
  children?: React.ReactNode;
  requestPersonInfo: RequestPersonInfo;
}

export const RequestLayout = ({
  children,
  requestPersonInfo,
}: RequestLayoutProps) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { requestId } = useParams<{ requestId: string }>();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openRequestDeletedModal, setOpenRequestDeletedModal] = useState(false);

  const { canEditRequest, canDeleteRequest } = useBusinessRule();

  const handleToggleConfirmModal = () => {
    setOpenConfirmModal((prevOpenConfirmModal) => !prevOpenConfirmModal);
  };

  const handleToggleRequestDeletedModal = () => {
    setOpenRequestDeletedModal(
      (prevOpenRequestDeletedModal) => !prevOpenRequestDeletedModal
    );
  };

  const handleBackToRequest = (isRegularRequest: boolean) => {
    history.push(
      isRegularRequest
        ? requestPersonInfo.requestTypeId === RequestTypeEnum.Preschool
          ? preschoolRequestsRoute
          : requestsRoute
        : subsequentRequestsRoute
    );
  };

  const handleEditRequest = (requestId: number) => {
    window.location.href =
      requestPersonInfo.requestTypeId === RequestTypeEnum.Preschool
        ? editPreschoolRequestUrl(requestId, requestPersonInfo.applicantOib)
        : editRequestUrl(requestId, requestPersonInfo.applicantOib);
  };

  const handleDeleteRequest = async (requestId: string) => {
    handleToggleConfirmModal();

    await deleteRequestApi(requestId).then(() => {
      handleToggleRequestDeletedModal();
    });
  };

  return (
    <>
      <Row className={styles.layout}>
        <Col md={12} className={styles.heading}>
          <Row className={styles.buttonLayout}>
            <div className={styles.buttonContent}>
              <div>
                <ArrowButton
                  ariaLabel={t("accessibility.backButton")}
                  backwards
                  onClick={() =>
                    handleBackToRequest(
                      requestPersonInfo.enrollmentTypeId ===
                        EnrollmentTypeEnum.RegularEnrollment
                    )
                  }
                ></ArrowButton>
              </div>
              <Typography
                variant="base"
                color="primary"
                className={styles.buttonText}
              >
                {t("request.backToRequestList")}
              </Typography>
            </div>
            <div className={styles.headerButtons}>
              {canEditRequest(
                requestPersonInfo.requestPedagogicalYearId,
                requestPersonInfo.requestStatusId,
                requestPersonInfo.enrollmentTypeId,
                requestPersonInfo.createdByAppUserId,
                requestPersonInfo.monthId
              ) && (
                <Button
                  size="sm"
                  outline
                  onClick={() => handleEditRequest(+requestId)}
                >
                  {t("request.editRequest")}
                </Button>
              )}
              {canDeleteRequest(
                requestPersonInfo.requestPedagogicalYearId,
                requestPersonInfo.requestStatusId,
                requestPersonInfo.enrollmentTypeId,
                requestPersonInfo.createdByAppUserId,
                requestPersonInfo.monthId
              ) && (
                <Button size="sm" outline onClick={handleToggleConfirmModal}>
                  {t("request.deleteRequest")}
                </Button>
              )}
            </div>
          </Row>
          <Row className={`${styles.nameLayout}`}>
            <div className={styles.nameContent}>
              <div>
                <FaceMan />
                <span className={styles.nameTitle}>
                  {`${requestPersonInfo.person?.firstName ?? ""} ${
                    requestPersonInfo.person?.lastName ?? ""
                  }`}
                </span>
              </div>
            </div>
          </Row>
          <Row className={styles.basicInformationLayout}>
            <Col md={4} className={styles.basicInformationContent}>
              <Typography
                variant="base"
                color="base"
                className={styles.spaceClass}
              >
                {t("person.oib")}: {requestPersonInfo.person?.oib}
              </Typography>
              <Typography
                variant="medium"
                color="base"
                className={styles.spaceClass}
              >
                {hrDate(requestPersonInfo.person?.dateOfBirth)}
              </Typography>
            </Col>
            <Col md={4} className={styles.informationLayout}>
              <TextField
                type="text"
                label={t("person.permanentResidence")}
                value={requestPersonInfo.person?.permanentResidenceInfo}
                className={styles.spaceClass}
                plaintext
              />
            </Col>
            <Col md={4} className={styles.informationLayout}>
              <TextField
                type="text"
                label={t("person.temporaryResidence")}
                value={requestPersonInfo.person?.temporaryResidenceInfo}
                className={styles.spaceClass}
                plaintext
              />
            </Col>
          </Row>
          <Row className={styles.basicInformationLayout}>
            <Col md={6} className={styles.informationLayout}>
              <TextField
                type="text"
                label={t("request.requestUid")}
                value={requestPersonInfo.requestUid}
                className={styles.spaceClass}
                plaintext
              />
            </Col>
            <Col md={6} className={styles.informationLayout}>
              <TextField
                  type="text"
                  label={t("request.requestCode")}
                  value={requestPersonInfo.code}
                  className={styles.spaceClass}
                  plaintext
              />
            </Col>
          </Row>
        </Col>
        <Col md={12} className={styles.borderClass}></Col>
        <Col md={12} className={styles.bodyClass}>
          {children}
        </Col>
      </Row>

      <Modal
        type={"confirm"}
        title={t("request.deleteRequestConfirmModalTitle")}
        open={openConfirmModal}
        confirmText={t("request.deleteRequestConfirmModalYes")}
        cancelText={t("common.no")}
        onClose={handleToggleConfirmModal}
        onConfirm={() => handleDeleteRequest(requestId)}
      >
        <div>{t("request.deleteRequestConfirmModalMessage")}</div>
      </Modal>

      <Modal
        type={"message"}
        open={openRequestDeletedModal}
        confirmText={t("common.close")}
        onConfirm={() =>
          handleBackToRequest(
            requestPersonInfo.enrollmentTypeId ===
              EnrollmentTypeEnum.RegularEnrollment
          )
        }
        onClose={() =>
          handleBackToRequest(
            requestPersonInfo.enrollmentTypeId ===
              EnrollmentTypeEnum.RegularEnrollment
          )
        }
      >
        <div className={styles.modalMessageContent}>
          <DeleteRequest className={styles.requestIcon} />
          <Typography variant="largeBold">
            {t("request.deleteRequestSuccessfullMessage")}
          </Typography>
        </div>
      </Modal>
    </>
  );
};
